import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Descriptions,
  List,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import {
  getMessengerPageSubscription,
  listMessengerRecentEvents,
  subscribeMessengerPage,
  unsubscribeMessengerPage,
  type MessengerPageSubscription,
  type MessengerWebhookEventRow,
} from '../services/api'

const { Text, Paragraph } = Typography

type Props = {
  metaPageId: string
  pageName?: string | null
}

export function MessengerPageSubscriptionPanel({ metaPageId, pageName }: Props) {
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [subscription, setSubscription] = useState<MessengerPageSubscription | null>(null)
  const [events, setEvents] = useState<MessengerWebhookEventRow[]>([])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [subRes, eventsRes] = await Promise.all([
        getMessengerPageSubscription(metaPageId),
        listMessengerRecentEvents(metaPageId, 15),
      ])
      setSubscription(subRes.subscription)
      setEvents(eventsRes.events || [])
    } catch (e: unknown) {
      message.error(
        (e as { response?: { data?: { message?: string } } }).response?.data?.message ||
          'Failed to load Page webhook settings'
      )
    } finally {
      setLoading(false)
    }
  }, [metaPageId])

  useEffect(() => {
    void load()
  }, [load])

  const subscribe = async () => {
    setBusy(true)
    try {
      const data = await subscribeMessengerPage(metaPageId)
      setSubscription(data.subscription)
      message.success('Subscribed to Page Messenger events')
      await load()
    } catch (e: unknown) {
      message.error(
        (e as { response?: { data?: { message?: string } } }).response?.data?.message ||
          'Subscribe failed'
      )
    } finally {
      setBusy(false)
    }
  }

  const unsubscribe = async () => {
    setBusy(true)
    try {
      const data = await unsubscribeMessengerPage(metaPageId)
      setSubscription(data.subscription)
      message.success('Unsubscribed from Page Messenger events')
      await load()
    } catch (e: unknown) {
      message.error(
        (e as { response?: { data?: { message?: string } } }).response?.data?.message ||
          'Unsubscribe failed'
      )
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return (
      <div style={{ padding: 12, textAlign: 'center' }}>
        <Spin size="small" />
      </div>
    )
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="small">
      <Alert
        type="info"
        showIcon
        message={`Page webhook settings — ${subscription?.metadata.name || pageName || metaPageId}`}
        description="Subscribe this Facebook Page to Messenger webhooks, then watch inbound message events arrive below."
      />
      <Descriptions bordered size="small" column={1}>
        <Descriptions.Item label="Page name">
          {subscription?.metadata.name || pageName || metaPageId}
        </Descriptions.Item>
        <Descriptions.Item label="About">{subscription?.metadata.about || '—'}</Descriptions.Item>
        <Descriptions.Item label="Category">
          {subscription?.metadata.category || '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Webhook URL">
          <Text code copyable={Boolean(subscription?.webhookUrl)}>
            {subscription?.webhookUrl || '—'}
          </Text>
        </Descriptions.Item>
        <Descriptions.Item label="Subscription">
          {subscription?.subscribed ? (
            <Tag color="green">Subscribed</Tag>
          ) : (
            <Tag color="orange">Not subscribed</Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Subscribed fields">
          {(subscription?.subscribedFields || []).length > 0
            ? subscription!.subscribedFields.join(', ')
            : '—'}
        </Descriptions.Item>
      </Descriptions>
      <Space wrap>
        <Button type="primary" loading={busy} onClick={() => void subscribe()}>
          Subscribe to Page events
        </Button>
        <Button danger loading={busy} onClick={() => void unsubscribe()}>
          Unsubscribe
        </Button>
        <Button icon={<ReloadOutlined />} onClick={() => void load()}>
          Refresh events
        </Button>
      </Space>
      <Paragraph style={{ color: '#64748b', marginBottom: 0 }}>
        Recent webhook events for this Page:
      </Paragraph>
      <List
        size="small"
        bordered
        dataSource={events}
        locale={{
          emptyText:
            'No webhook events yet — message this Page from Messenger to see one arrive.',
        }}
        renderItem={(ev) => (
          <List.Item>
            <List.Item.Meta
              title={
                <Space>
                  <Tag color={ev.direction === 'INBOUND' ? 'blue' : 'default'}>{ev.direction}</Tag>
                  <Text>{ev.participantName || `PSID …${ev.messengerPsid.slice(-6)}`}</Text>
                </Space>
              }
              description={`${new Date(ev.createdAt).toLocaleString()} — ${ev.text}`}
            />
          </List.Item>
        )}
      />
    </Space>
  )
}
